import { resolveWetlandAcreage } from '../reportFormatters';
/**
 * Environmental report section fallbacks (SNT-5).
 *
 * Pure deterministic 1-3 sentence fallbacks for the free-text fields on
 * the `environmental` report-type schema (siteRag.ts:1601-1676). Each
 * fallback returns a data-grounded statement composed from `sed` only —
 * no AI calls, no I/O, no Date.now / Math.random.
 *
 * Contract (mirrors `feasibility.ts`):
 *   1. Pure: same `site` / `sed` → same string.
 *   2. Data-grounded: NEVER use the instruction-shape regex patterns
 *      (recursion guard R6 pinned by `instructionShapeGuard.test.ts`).
 *      Concretely: avoid sentences shaped like "The X (should|must|...)
 *      (cite|reference|address|cover|...)" or bare imperatives like
 *      "Reference elevation, ...". Past-tense / declarative phrasing
 *      only.
 *   3. Returns a non-empty string. "Detailed narrative pending re-run."
 *      is the load-bearing closer that signals fallback status to the
 *      reader (SNT-3 amber banner reinforces).
 *
 * Composes with:
 *   - SNT-1 `instructionShapeGuard` — wraps every consumer.
 *   - SNT-2 `SectionQuarantineBanner` — renders when SNT-1 quarantines
 *     (in the delivered PDF / print tree; on the report page the header
 *     status chip names the section instead, since 2026-10-13).
 *   - PRV-1/2/3 lineage drawer — `[Ref N]`-style attribution preserved
 *     when the SED slot carries linkedDocumentIds.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { buildEnvironmentalBtmPermittingOutlookSummary } from '../buildEnvironmentalBtmPermittingOutlookSummary';

/* ───────────────────────── helpers ──────────────────────────── */

/**
 * Render protected-species list. Returns `'[—]'` when the section is
 * unavailable; never invents a placeholder species name.
 */
function speciesLabel(sed: SiteEvidenceDocument): string {
  const env = sed.environmental?.data;
  if (!env) return '[—]';
  if (!Array.isArray(env.species) || env.species.length === 0) return 'no listed species recorded';
  // Cap to first 3 to keep the bullet readable; full list lives in lineage.
  const head = env.species.slice(0, 3).join(', ');
  return env.species.length > 3 ? `${head}, +${env.species.length - 3} more` : head;
}

/**
 * Wetlands summary. Returns `'[—]'` if `environmental.data` is absent,
 * otherwise renders presence + acreage (when available).
 */
function wetlandsLabel(sed: SiteEvidenceDocument): string {
  const env = sed.environmental?.data;
  if (!env) return '[—]';
  if (env.hasWetlands === undefined) return 'wetland mapping coverage unresolved';
  if (env.hasWetlands === false) return 'no NWI wetlands intersected within screened coverage';
  const parts: string[] = [];
  if (env.wetlandType) parts.push(env.wetlandType);
  const acres = resolveWetlandAcreage(env);
  if (env.wetlandSourceAcreageConflicts?.length) parts.push('acreage unavailable: source records disagree');
  else if (env.wetlandAcreageIssues?.length) parts.push('acreage qualification unresolved');
  if (acres !== undefined) {
    parts.push(`${acres.toFixed(1)} ac${env.wetlandAcreageBasis ? ' full source features; not parcel acreage' : ''}`);
  }
  return parts.length > 0 ? `wetlands present (${parts.join(', ')})` : 'wetlands present';
}

/** FEMA flood-zone label, defensive against missing or empty string. */
function floodZoneLabel(sed: SiteEvidenceDocument): string {
  const fz = sed.flood?.data?.floodZone;
  if (typeof fz !== 'string' || fz.length === 0) return '[—]';
  return fz;
}

/** Air-quality summary. */
function airQualityLabel(sed: SiteEvidenceDocument): string {
  const aq = sed.airQuality?.data;
  if (!aq) return '[—]';
  const parts: string[] = [];
  if (typeof aq.aqi === 'number' && Number.isFinite(aq.aqi)) parts.push(`AQI ${aq.aqi}`);
  if (typeof aq.category === 'string' && aq.category.length > 0) parts.push(aq.category);
  if (parts.length === 0) return '[—]';
  return parts.join(' / ');
}

/* ───────────────────────── composers ────────────────────────── */

/**
 * Executive summary fallback for environmental reports — BTM Permitting
 * Outlook synthesis from live SED (seismic, water stress, nuclear infra,
 * wetlands/flood/tribal risks).
 */
export function environmentalExecutiveSummaryFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const summary = buildEnvironmentalBtmPermittingOutlookSummary(site, sed);
  if (summary.length > 120) return summary;
  return `${summary} Detailed narrative pending re-run.`;
}

/**
 * NEPA screening rationale fallback. Renders FEMA flood zone, presence
 * of critical habitat, and the species count. The "category" the SNT
 * pack calls out (CatEx vs EA vs EIS) is not in the SED today —
 * surfaced as `[—]` so the consumer sees the gap explicitly.
 */
export function nepaScreeningRationaleFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push('NEPA screening category: [—].');
  const env = sed.environmental?.data;
  if (env) {
    const ind: string[] = [];
    ind.push(`critical habitat: ${env.hasCriticalHabitat ? 'present' : 'none recorded'}`);
    ind.push(`listed species: ${speciesLabel(sed)}`);
    ind.push(`wetlands: ${wetlandsLabel(sed)}`);
    parts.push(`Indicators — ${ind.join('; ')}.`);
  }
  const fz = floodZoneLabel(sed);
  if (fz !== '[—]') parts.push(`FEMA flood zone ${fz}.`);
  parts.push('Detailed narrative pending re-run.');
  return parts.join(' ');
}

/**
 * ESA species assessment fallback. Reads USFWS environmental section.
 */
export function esaSpeciesAssessmentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const env = sed.environmental?.data;
  if (!env) return 'ESA species assessment pending re-run.';
  const parts: string[] = [];
  parts.push(`Critical habitat ${env.hasCriticalHabitat ? 'recorded within the screening buffer' : 'not recorded within the screening buffer'}.`);
  parts.push(`Listed species: ${speciesLabel(sed)}.`);
  parts.push('Section 7 consultation pathway pending re-run.');
  return parts.join(' ');
}

/**
 * Water rights assessment fallback (environmental schema). Pulls WRI
 * Aqueduct + presence of nearby water bodies. Distinct from the water-
 * resource report's deeper analysis — environmental version is a
 * screening summary.
 */
export function envWaterRightsAssessmentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const ws = sed.waterStress?.data;
  if (ws) {
    const stress = ws.stressLevel ?? '[—]';
    const score =
      typeof ws.bwsScore === 'number' && Number.isFinite(ws.bwsScore)
        ? ws.bwsScore.toFixed(1)
        : '[—]';
    parts.push(`WRI Aqueduct baseline water stress: ${stress} (${score} / 5).`);
  } else {
    parts.push('WRI Aqueduct baseline water stress: [—].');
  }
  const wf = sed.onSiteInfrastructure?.data?.waterFeatures;
  if (Array.isArray(wf) && wf.length > 0) {
    parts.push(`${wf.length} KMZ-tagged water feature(s) recorded near the parcel.`);
  }
  parts.push('Water-rights permitting detail pending re-run.');
  return parts.join(' ');
}

/**
 * FEMA flood analysis fallback. Reads `sed.flood.data`.
 */
export function femaFloodAnalysisFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const fd = sed.flood?.data;
  if (!fd) return 'FEMA flood analysis pending re-run.';
  const parts: string[] = [];
  parts.push(`Highest FEMA zone sampled: ${fd.floodZone ?? '[—]'}.`);
  parts.push(`Special Flood Hazard Area: ${fd.sfha ? 'yes' : 'no'}.`);
  if (typeof fd.sampledPoints === 'number' && fd.sampledPoints > 1) {
    parts.push(`${fd.sampledPoints} parcel sample points evaluated.`);
  }
  parts.push('Mitigation detail pending re-run.');
  return parts.join(' ');
}

/**
 * Tribal consultation fallback. The SED has no tribal-affiliation
 * provider today — render `[—]` and defer to the re-run.
 */
export function tribalConsultationFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Tribal consultation parties: [—]. SHPO coordination pathway pending re-run.';
}

/**
 * Land-use compatibility analysis fallback. Reads parcel + acreage.
 */
export function landUseCompatibilityFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const pd = sed.parcel?.data;
  if (pd) {
    if (pd.zoning) parts.push(`current zoning ${pd.zoning}`);
    if (pd.landUse) parts.push(`land use ${pd.landUse}`);
  }
  const sp = sed.siteProfile?.data;
  if (sp && typeof sp.acres === 'number' && Number.isFinite(sp.acres)) {
    parts.push(`${Math.round(sp.acres)} ac parcel`);
  }
  if (parts.length === 0) return 'Land-use compatibility pending re-run.';
  return `Parcel baseline: ${parts.join('; ')}. Compatibility narrative pending re-run.`;
}

/**
 * Environmental verdict (conclusion.environmentalVerdict). Neutral
 * placeholder — the deterministic scorer owns the actual verdict tier.
 */
export function environmentalVerdictFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Environmental verdict pending re-run.';
}

/**
 * RTU-2 (row 0.99235) — alternatives analysis fallback (NEPA §102(2)(E)).
 *
 * The SED today does not carry an "alternative sites" enumeration —
 * we surface the no-action baseline (always required), name the
 * dominant site constraint from the SED so a reviewer can see what
 * alternative-site triage would test against, and defer the rest to
 * the re-run.
 *
 * Phrasing discipline (recursion guard R6): past/declarative voice,
 * no "should cite" / "must address" / "reference X" verbs.
 */
export function alternativesAnalysisFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push('Alternatives reviewed: no-action baseline (status quo, grid-only supply).');
  const env = sed.environmental?.data;
  const fz = floodZoneLabel(sed);
  const stressLevel = sed.waterStress?.data?.stressLevel;
  const constraints: string[] = [];
  if (env?.hasCriticalHabitat) constraints.push('critical habitat');
  if (env?.hasWetlands) constraints.push('NWI wetlands');
  if (fz !== '[—]' && fz !== 'X') constraints.push(`FEMA zone ${fz}`);
  if (typeof stressLevel === 'string' && stressLevel.length > 0) {
    constraints.push(`WRI baseline water stress ${stressLevel}`);
  }
  if (constraints.length > 0) {
    parts.push(`Alternative-siting triage compares against the parcel's dominant constraint(s): ${constraints.join('; ')}.`);
  } else {
    parts.push('Dominant site constraints: [—].');
  }
  parts.push('Alternative technology / cooling-strategy comparison pending re-run.');
  return parts.join(' ');
}

/**
 * RTU-2 — cumulative impacts fallback (40 CFR §1508.7).
 *
 * The SED carries no explicit "foreseeable overlapping actions"
 * provider today. We surface the parcel-scale baselines a cumulative
 * review would weight against (watershed stress, baseline AQI,
 * adjacent land-use signal) and defer the cumulative roll-up to the
 * re-run.
 */
export function cumulativeImpactsFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const ws = sed.waterStress?.data;
  if (ws && typeof ws.bwsScore === 'number' && Number.isFinite(ws.bwsScore)) {
    parts.push(`Watershed baseline: WRI Aqueduct ${ws.stressLevel ?? '[—]'} (${ws.bwsScore.toFixed(1)} / 5).`);
  } else {
    parts.push('Watershed baseline: [—].');
  }
  const aq = airQualityLabel(sed);
  if (aq !== '[—]') parts.push(`Regional air-shed baseline: ${aq}.`);
  const adj = sed.parcel?.data?.landUse;
  if (typeof adj === 'string' && adj.length > 0) {
    parts.push(`Adjacent land-use signal: ${adj}.`);
  }
  parts.push('Foreseeable overlapping actions inventory pending re-run.');
  return parts.join(' ');
}

/**
 * RTU-2 — public participation plan fallback (40 CFR §1501.7 scoping
 * + EO 13175 tribal consultation).
 *
 * The SED has no tribal-affiliation or scoping-notice provider today —
 * surface the bare procedural skeleton ("scoping notice + comment
 * window + tribal coordination") as declarative placeholders so the
 * fallback never trips the instruction-shape guard, and defer the
 * specifics (stakeholder list, named tribes, comment-window dates) to
 * the re-run.
 */
export function publicParticipationPlanFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return (
    'Scoping notice: [—]. Public comment window: [—]. '
    + 'EO 13175 tribal consultation parties: [—] — coordination pathway pending re-run.'
  );
}
